import {
  CampaignSmPlatform,
  CbCompanyBudgetRange,
  CbTalentBudgetRange,
  MarketingChannel,
} from 'api/graphql-global-types';

export type PreferenceOption<TValue extends string> = {
  value: TValue;
  label: string;
};

/**
 * Budget buckets, the marketing mix and the platform list have no counterpart
 * in the backoffice, so they are spelled out here with the same wording the
 * brand sees in the campaign builder.
 *
 * Everything else (sports, gender, age, ethnicity, interests) comes from the
 * API - `useGetSports` and `useGetAudienceFilters` - so admin and brand always
 * pick from the same vocabulary.
 */
export const BUDGET_RANGE_OPTIONS: PreferenceOption<CbCompanyBudgetRange>[] = [
  { value: CbCompanyBudgetRange.UNDER_5K, label: 'Under $5,000' },
  { value: CbCompanyBudgetRange.FROM_5K_TO_25K, label: '$5,000 – $25,000' },
  { value: CbCompanyBudgetRange.FROM_25K_TO_50K, label: '$25,000 – $50,000' },
  { value: CbCompanyBudgetRange.FROM_50K_TO_100K, label: '$50,000 – $100,000' },
  {
    value: CbCompanyBudgetRange.FROM_100K_TO_250K,
    label: '$100,000 – $250,000',
  },
  {
    value: CbCompanyBudgetRange.FROM_250K_TO_500K,
    label: '$250,000 – $500,000',
  },
  { value: CbCompanyBudgetRange.FROM_500K_TO_1M, label: '$500,000 – $1M' },
  { value: CbCompanyBudgetRange.OVER_1M, label: '$1M+' },
];

export const TALENT_BUDGET_RANGE_OPTIONS: PreferenceOption<CbTalentBudgetRange>[] =
  [
    { value: CbTalentBudgetRange.UNDER_250, label: 'Under $250' },
    { value: CbTalentBudgetRange.FROM_250_TO_500, label: '$250 - $500' },
    { value: CbTalentBudgetRange.FROM_500_TO_1K, label: '$500 - $1,000' },
    { value: CbTalentBudgetRange.FROM_1K_TO_2_5K, label: '$1,000 - $2,500' },
    { value: CbTalentBudgetRange.FROM_2_5K_TO_10K, label: '$2,500 - $10,000' },
    { value: CbTalentBudgetRange.FROM_10K_TO_25K, label: '$10,000 - $25,000' },
    {
      value: CbTalentBudgetRange.FROM_25K_TO_100K,
      label: '$25,000 - $100,000',
    },
    { value: CbTalentBudgetRange.OVER_100K, label: '$100K+' },
  ];

export const MARKETING_CHANNEL_OPTIONS: PreferenceOption<MarketingChannel>[] = [
  { value: MarketingChannel.CONTENT_CREATION, label: 'Content Creation' },
  {
    value: MarketingChannel.SOCIAL_COLLABORATIONS,
    label: 'Social Collaborations',
  },
  { value: MarketingChannel.EVENT_SPONSORSHIPS, label: 'Event Sponsorships' },
  { value: MarketingChannel.BRAND_AMBASSADORS, label: 'Brand Ambassadors' },
  { value: MarketingChannel.AFFILIATE_MARKETING, label: 'Affiliate Marketing' },
  {
    value: MarketingChannel.INFLUENCER_MARKETING,
    label: 'Influencer Marketing',
  },
  { value: MarketingChannel.PRODUCT_PLACEMENT, label: 'Product Placement' },
  { value: MarketingChannel.NIL_DEALS, label: 'NIL Deals' },
  { value: MarketingChannel.PAID_MEDIA, label: 'Paid Media' },
  {
    value: MarketingChannel.EXPERIENTIAL_MARKETING,
    label: 'Experiential Marketing',
  },
];

/** Only the platforms brands book talent on, same as the campaign builder. */
export const PLATFORM_OPTIONS: PreferenceOption<CampaignSmPlatform>[] = [
  { value: CampaignSmPlatform.IG, label: 'Instagram' },
  { value: CampaignSmPlatform.TT, label: 'TikTok' },
];

/** ISO 639-1 codes, matched against what the audience report holds. */
export const AUDIENCE_LANGUAGE_OPTIONS: PreferenceOption<string>[] = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'zh', label: 'Mandarin' },
  { value: 'ar', label: 'Arabic' },
  { value: 'hi', label: 'Hindi' },
];
